import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { BYPASS_AUTH_INTERCEPTOR } from './http-interceptor';
import { clearLocalStorageExcept } from './utils/utils';

// Marks a request already replayed once after a token refresh, so a second 401
// is treated as a genuine session end rather than retried forever.
const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(protected keycloakService: KeycloakService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          // Unauthenticated requests can't be fixed by a token refresh.
          if (request.context.get(BYPASS_AUTH_INTERCEPTOR) || request.context.get(AUTH_REFRESH_RETRIED)) {
            this.sendToLogin();
            return throwError(() => err);
          }
          return this.refreshAndRetry(request, next, err);
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * A 401 usually means the access token expired while the timer-based renewal
   * (OnTokenExpired -> updateToken in KeycloakWrapperService) was throttled in a
   * background tab or failed once. The refresh token is normally still valid, so
   * refresh and replay the request instead of bouncing the user to Keycloak; only
   * a failed refresh or a second 401 (a genuine session end) redirects to login.
   */
  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalErr: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    return from(this.keycloakService.updateToken(5)).pipe(
      switchMap(() => {
        const instance = this.keycloakService.getKeycloakInstance();
        const token = instance?.token;
        if (!token) {
          this.sendToLogin();
          return throwError(() => originalErr);
        }
        sessionStorage.setItem('token', token);
        if (instance.refreshToken) sessionStorage.setItem('refreshToken', instance.refreshToken);

        request.context.set(AUTH_REFRESH_RETRIED, true);
        const retried = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
        return next.handle(retried).pipe(
          catchError((retryErr) => {
            // still 401 with a freshly refreshed token -> genuine session end
            if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
              this.sendToLogin();
            }
            return throwError(() => retryErr);
          }),
        );
      }),
      catchError((err) => {
        // HttpErrorResponses come from the retried request and are already handled above;
        // anything else is updateToken itself rejecting, which means the refresh token is
        // dead and the session is over.
        if (err instanceof HttpErrorResponse) {
          return throwError(() => err);
        }
        this.sendToLogin();
        return throwError(() => originalErr);
      }),
    );
  }

  private sendToLogin() {
    console.log(`TokenInterceptor sendToLogin: clearLocalStorageExcept`);
    clearLocalStorageExcept([
      'account',
      'transactions',
      'beneficiary',
      'notifications',
      'lastLoggedInId',
      'APP_PREFERENCES',
      'firstTime',
      'lastSelectedOrg',
    ]);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    this.keycloakService.login({
      redirectUri: window.location.href,
    });
  }
}
